import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import Icon from 'components/DesignSystem/Icon';
import { addHours, parse, format } from 'date-fns';

import { DATE_FORMAT, DisplayMode, IMAGE_DATE_REGEX } from 'helper/products/synopticCharts';
import {
  selectDisplayMode,
  selectImageIndex,
  selectImages,
  selectImagesAreLoading,
  setDisplayMode,
  setImageIndex,
} from 'redux/reducers/synopticChartsReducer';
import Slider from 'components/DesignSystem/Slider';
import { cssVar } from 'constants/theme';

import './index.scss';

export default function ForecastDaySlider() {
  const dispatch = useDispatch();
  const displayMode = useSelector(selectDisplayMode);

  const data = useSelector(selectImages);
  const isFetching = useSelector(selectImagesAreLoading);

  const selectedIndex = useSelector(selectImageIndex);
  const selectedDate = data && data[selectedIndex];

  const dataIsAvailable = data && data.length > 0;
  const disabled = displayMode !== DisplayMode.Images || isFetching || !dataIsAvailable;

  const minIndex = 0;
  const maxIndex = data ? data.length - 1 : 0;

  const match = selectedDate && selectedDate.date.match(IMAGE_DATE_REGEX);
  const baseDate = match?.[1];
  const offset = match?.[2];
  const parsedBaseDate = baseDate && parse(baseDate, DATE_FORMAT, new Date());
  const parsedOffset = offset && parseInt(offset, 10);
  const dateWasParsed = parsedBaseDate instanceof Date && typeof parsedOffset === 'number';
  const parsedFinalDate = dateWasParsed && addHours(parsedBaseDate, parsedOffset);
  const thumbLabel = parsedFinalDate ? format(parsedFinalDate, "dd.MM - HH'h'") : '';

  const handleEnter = () => {
    dispatch(setDisplayMode(DisplayMode.Images));
  };

  const handleChange = (i: number) => {
    dispatch(setImageIndex(i));
  };

  const firstIndex = () => {
    handleChange(minIndex);
  };
  const previousIndex = () => {
    const newIndex = Math.max(selectedIndex - 1, minIndex);
    handleChange(newIndex);
  };
  const nextIndex = () => {
    const newIndex = Math.min(selectedIndex + 1, maxIndex);
    dispatch(setImageIndex(newIndex));
  };
  const lastIndex = () => {
    handleChange(maxIndex);
  };

  return (
    <div
      onClick={handleEnter}
      onKeyDown={handleEnter}
      onFocus={handleEnter}
      role="button"
      aria-label="index-buttons"
      tabIndex={0}
      className={`
        synoptic-charts-range-control
        ${disabled ? 'deselected' : ''}
      `}
    >
      <Button variant="link" onClick={firstIndex}>
        <Icon
          className="synoptic-charts-slider-control-button"
          iconType="double_left_arrow"
          color={cssVar('text-link')}
        />
      </Button>
      <Button variant="link" onClick={previousIndex}>
        <Icon
          className="synoptic-charts-slider-control-button"
          iconType="left_arrow"
          color={cssVar('text-link')}
        />
      </Button>
      <Slider
        min={0}
        max={maxIndex}
        value={selectedIndex}
        setValue={handleChange}
        renderOnThumb={() => thumbLabel}
        className="synoptic-charts-horizontal-slider"
      />
      <Button variant="link" onClick={nextIndex}>
        <Icon
          className="synoptic-charts-slider-control-button"
          iconType="right_arrow"
          color={cssVar('text-link')}
        />
      </Button>
      <Button variant="link" onClick={lastIndex}>
        <Icon
          className="synoptic-charts-slider-control-button"
          iconType="double_right_arrow"
          color={cssVar('text-link')}
        />
      </Button>
    </div>
  );
}
